import React, { useState, useContext, useCallback } from 'react';
import AdminContext from './index';
import * as AdminAPI from '../../api/admin';
import { checkRefreshToken } from '../../api/refreshToken';

export function useAuth() {
  return useContext(AdminContext);
}

function useAdminProviderAuth() {
  const [admin, setAdmin] = useState(null);
  const [status, setStatus] = useState('NOT_LOGIN');
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Hàm đăng nhập - Gọi API admin.js
  const login = useCallback(async (username, password) => {
    setIsLoading(true);
    setError(null);
    setStatus('LOADING');

    try {
      const result = await AdminAPI.login(username, password);

      if (result.admin) {
        setAdmin(result.admin);
        setIsAuthenticated(true);
        setStatus('LOGGED_IN');

        return {
          message: 'Đăng nhập thành công',
          accessToken: result.token || result.accessToken,
          refreshToken: result.refreshToken,
          admin: result.admin,
        };
      }

      setError('Login failed. Please try again.');
      throw new Error('Login failed.');
    } catch (err) {
      setError(err.message || 'Login failed. Please try again.');
      setIsAuthenticated(false);
      setStatus('ERROR');
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Kiểm tra Authentication - Gọi API getAdminById
  // ⚙️ AUTO REFRESH TOKEN FLOW:
  // 1. Gọi getAdminById() với token hiện tại
  // 2. Nếu token hết hạn (401), interceptor trong admin.js sẽ TỰ ĐỘNG:
  //    - Dùng refresh token để lấy access token mới
  //    - Retry request ban đầu với token mới
  //    - Nếu refresh token cũng hết hạn → logout và redirect về /login
  // 3. AdminProvider chỉ cần nhận kết quả, KHÔNG can thiệp vào flow refresh token
  const checkAuth = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    setStatus('LOADING');

    try {
      const adminId = localStorage.getItem('adminId');
      const authToken = localStorage.getItem('authToken');
      

      if (!adminId || !authToken) {
        setStatus('NOT_LOGIN');
        setIsAuthenticated(false);
        setAdmin(null);
        return;
      }

      // Interceptor sẽ tự động refresh token nếu hết hạn
      const fetchedAdmin = await AdminAPI.getAdminById(adminId);

      if (fetchedAdmin) {
        setAdmin(fetchedAdmin);
        setIsAuthenticated(true);
        setStatus('LOGGED_IN');
      } else {
        setError('Failed to authenticate');
        setIsAuthenticated(false);
        setStatus('ERROR');
      }
    } catch (err) {
      console.error('❌ AdminProvider - checkAuth error:', err);
      
      // ⚠️ QUAN TRỌNG: KHÔNG clear localStorage ở đây!
      // Interceptor trong admin.js đã tự động xử lý:
      // - Refresh token khi gặp 401
      // - Logout và clear localStorage nếu refresh fail
      // AdminProvider chỉ cần reset state để UI cập nhật
      
      setError('Authentication failed. Please try again.');
      setIsAuthenticated(false);
      setStatus('NOT_LOGIN');
      setAdmin(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Check token - Gọi API refreshToken (Manual check, interceptor sẽ auto handle)
  const checkToken = useCallback(async () => {
    setStatus('LOADING');

    try {
      const tokenOld = localStorage.getItem('authToken');
      const refreshTokenOld = localStorage.getItem('refresh_token');

      if (!tokenOld || !refreshTokenOld) {
        setStatus('NOT_LOGIN');
        setIsAuthenticated(false);
        setAdmin(null);
        return;
      }

      const result = await checkRefreshToken(tokenOld, refreshTokenOld);

      if (result.message === 'Access token hợp lệ') {
        setStatus('LOGGED_IN');
        setIsAuthenticated(true);
      } else if (result.accessToken) {
        setStatus('LOGGED_IN');
        setIsAuthenticated(true);
      } else {
        setStatus('NOT_LOGIN');
        setIsAuthenticated(false);
      }
    } catch (err) {
      console.error('❌ Lỗi khi kiểm tra hoặc làm mới token:', err);
      setStatus('NOT_LOGIN');
      setIsAuthenticated(false);
      setAdmin(null);
      
      // CHÚ Ý: KHÔNG clear tokens ở đây
      // Để interceptor trong admin.js tự động xử lý refresh token và logout nếu cần
    }
  }, []);

  // Hàm logout
  const logout = useCallback(() => {
    localStorage.removeItem('adminId');
    localStorage.removeItem('authToken');
    localStorage.removeItem('refresh_token');

    setAdmin(null);
    setIsAuthenticated(false);
    setStatus('NOT_LOGIN');
  }, []);

  return {
    admin,
    status,
    error,
    isLoading,
    isAuthenticated,
    login,
    checkAuth,
    logout,
    checkToken,
    role_code: admin?.role_code,
  };
}

const AdminProvider = ({ children }) => {
  const auth = useAdminProviderAuth();
  return <AdminContext.Provider value={auth}>{children}</AdminContext.Provider>;
};

export default AdminProvider;

